import s3Client from './s3Client';
import uuid from 'uuid/v4';

// Eliminamos la dependencia de REACT_APP_S3_BUCKET
// const BUCKET = process.env.REACT_APP_S3_BUCKET;

// const getFilenameFromUrl = url => url.substring(url.lastIndexOf('/') + 1);

// Función para sanitizar nombres de archivo
const sanitizeFileName = (fileName) => {
  if (!fileName) return "";
  return fileName
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "") // Elimina acentos
    .replace(/[^a-zA-Z0-9.-]/g, "-") // Reemplaza caracteres especiales por guiones
    .replace(/--+/g, "-") // Reemplaza múltiples guiones por uno solo
    .replace(/^-+|-+$/g, ""); // Elimina guiones al inicio y final
};

const convertUrlToFileInput = (value) => {
  if (!value) return null;
  if (Array.isArray(value)) {
    return value.map((url) => ({ src: url }));
  }
  return { src: value };
};

// Eliminamos esta función que usaba REACT_APP_S3_BUCKET
// const resourceFileUrl = (file) =>
//   `https://${BUCKET}.s3.amazonaws.com/${file.title}`;

const imageFilenameGenerator = (folder) => (file) => {
  // If file has a rawFile, generate a new name with UUID
  if (file.rawFile) {
    const originalName = file.rawFile.name || "file";
    const extensionPosition = originalName.lastIndexOf(".");
    const extension = extensionPosition
      ? originalName.substring(extensionPosition)
      : "";
    const sanitizedName = sanitizeFileName(
      originalName.substring(0, extensionPosition)
    );
    return `${folder}/${sanitizedName}-${uuid()}${extension}`;
  }

  // If file has a src (URL), extract filename from it
  if (file.src) {
    const fileName = file.src.substring(file.src.lastIndexOf("/") + 1);
    return `${folder}/${sanitizeFileName(fileName)}`;
  }

  // Fallback: generate a new name for other cases (should be rare)
  return `${folder}/${uuid()}`;
};

/**
 * Procesa un campo de archivo (único o múltiple), subiendo los archivos nuevos
 * y manteniendo las URLs de los existentes.
 * @param {File | File[] | String | String[]} files - El valor del campo del formulario.
 * @param {Function} s3KeyGenerator - Una función que toma un objeto File y devuelve el S3 key completo.
 * @returns {Promise<String | String[] | null>} - La URL o URLs finales.
 */
const processFilesForField = async (files, s3KeyGenerator) => {
  if (!files) return Array.isArray(files) ? [] : null;

  const filesArray = Array.isArray(files) ? files : [files];
  const finalUrls = [];
  const newFilesToUpload = [];

  console.log("Processing files for field:", {
    totalFiles: filesArray.length,
    files: filesArray.map(f => ({
      hasRawFile: !!f?.rawFile,
      hasSrc: !!f?.src,
      isString: typeof f === 'string'
    }))
  });

  for (const file of filesArray) {
    // Es un archivo nuevo para subir
    if (file && file.rawFile) {
      const processedFile = {
        ...file,
        title: s3KeyGenerator(file), // Genera el S3 key completo
      };
      newFilesToUpload.push(processedFile);
      console.log("Added new file for upload:", processedFile.title);
      // No construimos la URL aquí, la obtendremos del resultado de s3Client.upload
    }
    // Es una URL existente, se mantiene
    else if (typeof file === "string" && file) {
      finalUrls.push(file);
      console.log("Keeping existing URL:", file);
    }
    // Es un objeto de archivo existente (sin rawFile), se extrae la URL
    else if (file && file.src) {
      finalUrls.push(file.src);
      console.log("Extracting URL from existing file:", file.src);
    }
  }

  // Sube todos los archivos nuevos en paralelo y obtiene las URLs reales
  if (newFilesToUpload.length > 0) {
    console.log("Starting upload of", newFilesToUpload.length, "new files");
    try {
      const uploadedUrls = await s3Client.upload(newFilesToUpload);
      console.log("Upload completed successfully:", uploadedUrls);
      finalUrls.push(...uploadedUrls);
    } catch (error) {
      console.error("Failed to upload files:", error);
      throw new Error(`Failed to upload files: ${error.message}`);
    }
  } else {
    console.log("No new files to upload");
  }

  console.log("Final URLs for field:", finalUrls);

  // Devuelve el mismo tipo de dato que recibió (string o array)
  return Array.isArray(files) ? finalUrls : finalUrls[0] || null;
};

const processFiles = async (data, resource) => {
  if (!data) return data;

  switch (resource) {
    case "products": {
      const product = data;
      
      console.log("Processing product files:", {
        productCode: product.code,
        hasManual: !!product.manual,
        hasImages: !!product.images,
        hasHdImages: !!product.hdImages,
        hasCatalogImages: !!product.catalogImages
      });

      // Validar que el código del producto existe
      if (!product.code) {
        console.error("Product code is missing:", product);
        throw new Error("Product code is required for file upload");
      }

      // Generador de S3 keys específico para productos
      const productS3KeyGenerator = (folder) => (file) => {
        const sanitizedCode = sanitizeFileName(product.code);
        const generatedKey = imageFilenameGenerator(folder)(file);
        const finalKey = `products/${sanitizedCode}/${generatedKey}`;
        
        console.log("Generated S3 key for product:", {
          folder,
          originalCode: product.code,
          sanitizedCode,
          generatedKey,
          finalKey
        });
        
        return finalKey;
      };

      console.log("Starting parallel upload for product fields");
      
      const [manualUrl, imagesUrls, hdImagesUrls, catalogImagesUrls] =
        await Promise.all([
          processFilesForField(
            product.manual,
            productS3KeyGenerator("manuals")
          ),
          processFilesForField(product.images, productS3KeyGenerator("images")),
          processFilesForField(product.hdImages, productS3KeyGenerator("hd")),
          processFilesForField(
            product.catalogImages,
            productS3KeyGenerator("catalog")
          ),
        ]);

      console.log("Product upload completed:", {
        manualUrl,
        imagesUrls,
        hdImagesUrls,
        catalogImagesUrls
      });

      return {
        ...data,
        manual: manualUrl,
        images: imagesUrls,
        hdImages: hdImagesUrls,
        catalogImages: catalogImagesUrls,
      };
    }

    case "servicerequests": {
      const keyGenerator = imageFilenameGenerator("servicerequests");
      const invoiceImageUrl = await processFilesForField(
        data.invoiceImage,
        keyGenerator
      );
      return {
        ...data,
        invoiceImage: invoiceImageUrl,
      };
    }

    case "banners":
    case "sellers":
    case "articles":
    case "categories": {
      const keyGenerator = imageFilenameGenerator(resource);
      const imageUrl = await processFilesForField(data.image, keyGenerator);
      return {
        ...data,
        image: imageUrl,
      };
    }

    default:
      return data;
  }
};

export { convertUrlToFileInput, processFiles }; 